import * as React from 'react';
import {FunctionComponent, useMemo, useState} from 'react';
import {DownloadedIndoorWorkout} from '../../../../../Models/DownloadedIndoorWorkout';
import {classNames} from '../../../../Support/classNames';
import {SingleStrokeAnalyzer} from './SingleStrokeAnalyzer';
import {ExtendedSingleStrokeAnalyzer} from './ExtendedSingleStrokeAnalyzer';
import {FeatureFlags} from '../../../../../FeatureFlags';
import {RangeStrokeAnalyzer} from './RangeStrokeAnalyzer';
import {ExtendedRangeStrokeAnalyzer} from './ExtendedRangeStrokeAnalyzer';
import {Duration} from '../../../../../Domain/Duration';
import {downloadedIndoorWorkoutToStrokes} from '../../../../../Strokes/downloadedIndoorWorkoutToStrokes';
import {InfoTooltip} from '../../../Utils/InfoTooltip';
import {RangeInput} from '../../../../../Models/RangeInput';

interface OwnProps {
    downloadedIndoorWorkout: DownloadedIndoorWorkout;
    range: RangeInput | null;
    onRangeChange?: (strokeRange: RangeInput | null) => void;
    className?: string;
}

export const StrokeAnalyzer: FunctionComponent<OwnProps> = ({
    downloadedIndoorWorkout,
    range,
    onRangeChange,
    className,
}) => {
    const strokes = useMemo(() => downloadedIndoorWorkoutToStrokes(downloadedIndoorWorkout), [downloadedIndoorWorkout]);
    const [showReferenceCurve, setShowReferenceCurve] = useState(false);

    if (strokes.length === 0) {
        return (
            <div className={classNames('card', className)}>
                <div className="card-title">
                    <h5>Stroke analyzer</h5>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-12 d-flex">
                            <span className="card-body__empty">No stroke data available</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classNames('card', className)} id="stroke-analyzer">
            <div className="card-title">
                {FeatureFlags.extendedStrokeAnalyzer ? (
                    <h5>
                        Advanced stroke analyzer
                        <InfoTooltip
                            className="ms-2"
                            description="Based on individual strokes, can differ from interval results"
                        />
                    </h5>
                ) : (
                    <h5>
                        Stroke analyzer
                        <InfoTooltip
                            className="ms-2"
                            description="Based on individual strokes, can differ from interval results"
                        />
                    </h5>
                )}
            </div>
            <div className="card-body">
                {range === null &&
                    (FeatureFlags.extendedStrokeAnalyzer && showReferenceCurve ? (
                        <ExtendedSingleStrokeAnalyzer strokes={strokes} />
                    ) : (
                        <SingleStrokeAnalyzer strokes={strokes} />
                    ))}
                {range !== null &&
                    (FeatureFlags.extendedStrokeAnalyzer && showReferenceCurve ? (
                        <ExtendedRangeStrokeAnalyzer strokes={strokes} range={range} onRangeChange={onRangeChange} />
                    ) : (
                        <RangeStrokeAnalyzer strokes={strokes} range={range} onRangeChange={onRangeChange} />
                    ))}

                {!FeatureFlags.strokeAnalyzer && onRangeChange && (
                    <div className="row d-none d-lg-flex">
                        <div className="col-12 col-md-4 skeleton skeleton--static">
                            <div className="btn-group mt-3" role="group">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="extendedModeCheckbox"
                                    />
                                    <label className="form-check-label" htmlFor="extendedModeCheckbox">
                                        Show reference curve
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4 d-flex flex-row justify-content-center skeleton skeleton--static">
                            <div className="btn-group m-2" role="group">
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="stroke-analyzer-mode"
                                    id="single"
                                    autoComplete="off"
                                    value="single"
                                />
                                <label className="btn btn-outline-primary" htmlFor="single">
                                    Single
                                </label>
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="stroke-analyzer-mode"
                                    id="range"
                                    autoComplete="off"
                                    value="range"
                                />
                                <label className="btn btn-outline-primary" htmlFor="range">
                                    Range
                                </label>
                            </div>
                        </div>
                        <div className="col-4 d-flex justify-content-end align-items-center">
                            {FeatureFlags.isAdvancedSubscriptionsEnabled && (
                                <p>
                                    Analyse ranges of strokes. Become an{' '}
                                    <a href="/account/subscriptions/checkout">advanced member</a>.
                                </p>
                            )}
                            {!FeatureFlags.isAdvancedSubscriptionsEnabled && (
                                <p>
                                    Analyse ranges of strokes. Join the <a href="/beta">beta</a>.
                                </p>
                            )}
                        </div>
                    </div>
                )}
                {FeatureFlags.strokeAnalyzer && onRangeChange && (
                    <div className="row">
                        <div className="col-12 col-md-4">
                            {FeatureFlags.extendedStrokeAnalyzer && (
                                <div className="btn-group mt-3" role="group">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="extendedModeCheckbox"
                                            checked={showReferenceCurve}
                                            onChange={() => setShowReferenceCurve((v) => !v)}
                                        />
                                        <label className="form-check-label" htmlFor="extendedModeCheckbox">
                                            Show reference curve
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-12 col-md-4 d-flex flex-row justify-content-center">
                            <div className="btn-group m-2" role="group">
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="stroke-analyzer-mode"
                                    id="single"
                                    autoComplete="off"
                                    value="single"
                                    onChange={(e) => {
                                        if (onRangeChange) {
                                            onRangeChange(null);
                                        }
                                    }}
                                    checked={range === null}
                                />
                                <label className="btn btn-outline-primary" htmlFor="single">
                                    Single
                                </label>
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="stroke-analyzer-mode"
                                    id="range"
                                    autoComplete="off"
                                    value="range"
                                    onChange={(e) => {
                                        if (onRangeChange) {
                                            onRangeChange({
                                                start: Duration.zero(),
                                                end: strokes[strokes.length - 1].totalTime,
                                                predetermined: {},
                                            });
                                        }
                                    }}
                                    checked={range !== null}
                                />
                                <label className="btn btn-outline-primary" htmlFor="range">
                                    Range
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
